import { api } from './api';

/**
 * Admin Authentication Service
 * Handles all admin-specific authentication-related API calls and token management
 */
const adminAuthService = {
  /**
   * Login with admin email and password
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise} - Promise with admin user data
   */
  login: async (email, password) => {
    try {
      console.log('Admin login payload:', { email, password: password ? '********' : null });
      
      const response = await api.post('/api/auth/admin/login', { 
        email, 
        password,
        isAdmin: true // Explicitly mark this as an admin login
      });
      console.log('Admin login response:', response.data);
      
      if (response.data.data && response.data.data.token) {
        // Store admin token and user data separately from regular user
        localStorage.setItem('adminAuthToken', response.data.data.token);
        localStorage.setItem('adminUser', JSON.stringify(response.data.data.user));
      }
      return response;
    } catch (error) {
      console.error('Admin login error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Logout the current admin user
   */
  logout: () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    // Optionally call the logout endpoint if needed
    return api.post('/api/auth/logout');
  },

  /**
   * Get the current authenticated admin user
   * @returns {Promise} - Promise with current admin user data
   */
  getCurrentUser: async () => {
    try {
      // Add admin token to request headers
      const token = localStorage.getItem('adminAuthToken');
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('Get current admin user error:', error);
      throw error;
    }
  },

  /**
   * Update current admin user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} - Promise with success message
   */
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/auth/update-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update admin password error:', error);
      throw error;
    }
  },

  /**
   * Check if admin user is authenticated
   * @returns {boolean} - True if authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('adminAuthToken');
  },

  /**
   * Get current admin user from localStorage
   * @returns {Object|null} - Admin user object or null
   */
  getUser: () => {
    const user = localStorage.getItem('adminUser');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Check if current user is an admin
   * @returns {boolean} - True if user is admin
   */
  isAdmin: () => {
    const user = adminAuthService.getUser();
    return user && user.role === 'admin';
  },

  /**
   * Get admin authentication token
   * @returns {string|null} - Admin token or null
   */
  getToken: () => {
    return localStorage.getItem('adminAuthToken');
  }
};

export default adminAuthService;
